import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { trashIcon } from '@progress/kendo-svg-icons';
import { DlpLookupModel, DlpScopeModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { DlpIssue, scopeStatus } from 'utils/dlpConfigurationValidation';
import { DlpField } from '../Common/DlpField';
import { ChipToggle } from '../Common/ChipMultiSelect';
import { IssueList } from '../Common/IssueList';
import { SaveBar } from '../Common/SaveBar';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { EditableTitle } from '../Common/EditableTitle';
import { LookupMultiSelect } from '../Common/LookupMultiSelect';
import { ScopeStatusBadge } from '../Common/ScopeStatusBadge';
import { useReportDirty } from '../Common/useUnsavedChangesGuard';
import { ScopeBrandImport } from './ScopeBrandImport';

const sameBrands = (a: string[], b: string[]) => JSON.stringify([...a].sort((left, right) => left.localeCompare(right))) === JSON.stringify([...b].sort((left, right) => left.localeCompare(right)));

interface ScopeDetailProps {
    scope: DlpScopeModel;
    brandIds: string[];
    // Brands already assigned to other scopes of the same project
    brandsUsedElsewhere: string[];
    profileNames: string[];
    brands: DlpLookupModel[];
    countries: DlpLookupModel[];
    issues: DlpIssue[];
    // Not saved yet: it only exists in the page until the first save
    isNew: boolean;
    onSave: (scope: DlpScopeModel, brandIds: string[]) => Promise<void>;
    onDelete: (scopeId: number) => Promise<void>;
    onDirtyChange: (dirty: boolean) => void;
}

// Mount with key={scope.scope_id}: the draft is initialised once per scope.
export function ScopeDetail({ scope, brandIds, brandsUsedElsewhere, profileNames, brands, countries, issues, isNew, onSave, onDelete, onDirtyChange }: ScopeDetailProps) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<DlpScopeModel>(scope);
    const [draftBrands, setDraftBrands] = useState<string[]>(brandIds);
    const [confirm, setConfirm] = useState<'save' | 'delete' | null>(null);

    const patch = (changes: Partial<DlpScopeModel>) => setDraft(d => ({ ...d, ...changes }));
    const isDirty = isNew || JSON.stringify(draft) !== JSON.stringify(scope) || !sameBrands(draftBrands, brandIds);
    useReportDirty(isDirty, onDirtyChange);

    const save = async () => {
        setConfirm(null);
        // An "all brands" scope is a single row with no brand
        const brandsToSave = draft.all_brands ? [] : draftBrands;
        setDraftBrands(brandsToSave);
        await onSave(draft, brandsToSave);
    };

    const remove = async () => {
        setConfirm(null);
        await onDelete(scope.scope_id);
    };

    return (
        <div className="detail-col">
            <div className="detail-header">
                <div style={{ flex: 1 }}>
                    <EditableTitle
                        value={draft.name}
                        onChange={name => patch({ name })}
                        onCancel={() => patch({ name: scope.name })}
                        placeholder={t('dlpConfiguration.scopes.namePlaceholder')}
                    >
                        <ScopeStatusBadge status={scopeStatus(scope)} />
                    </EditableTitle>
                    <div className="detail-sub">
                        {draft.all_brands ? t('dlpConfiguration.scopes.appliesToAllBrands') : t('dlpConfiguration.scopes.brandCount', { count: draftBrands.length })}
                    </div>
                </div>
                <div className="detail-actions">
                    <button type="button" className="lm-btn-danger sm" onClick={() => setConfirm('delete')}>
                        <SvgIcon icon={trashIcon} size="small" /> {t('general.delete')}
                    </button>
                </div>
            </div>

            <IssueList issues={issues} />

            <div className="form-grid">
                <DlpField label={t('dlpConfiguration.scopes.quotaProfile')}>
                    <select className="lm-input" value={draft.quota_profile} onChange={e => patch({ quota_profile: e.target.value })}>
                        <option value="">{t('dlpConfiguration.scopes.selectProfile')}</option>
                        {profileNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </DlpField>
                <DlpField label={t('dlpConfiguration.scopes.startFrom')}>
                    <input className="lm-input" type="date" value={draft.start_from || ''} onChange={e => patch({ start_from: e.target.value })} />
                </DlpField>
                <DlpField label={t('dlpConfiguration.scopes.endedAt')} hint={t('dlpConfiguration.scopes.endedAtHint')}>
                    <input className="lm-input" type="date" value={draft.ended_at || ''} onChange={e => patch({ ended_at: e.target.value || null })} />
                </DlpField>
            </div>

            <DlpField label={t('dlpConfiguration.scopes.appliesTo')}>
                <div className="chip-row">
                    <ChipToggle label={t('dlpConfiguration.scopes.specificBrands')} active={!draft.all_brands} onClick={() => patch({ all_brands: false })} />
                    <ChipToggle label={t('dlpConfiguration.scopes.allBrands')} active={draft.all_brands} onClick={() => patch({ all_brands: true })} />
                </div>
            </DlpField>

            {!draft.all_brands && (
                <DlpField label={t('dlpConfiguration.scopes.brands')} hint={t('dlpConfiguration.scopes.brandsHint')}>
                    <LookupMultiSelect
                        options={brands}
                        value={draftBrands}
                        onChange={setDraftBrands}
                        warnIds={brandsUsedElsewhere}
                        warnTag={t('dlpConfiguration.scopes.brandInOtherScope')}
                        placeholder={t('dlpConfiguration.scopes.selectBrands')}
                    />
                    <ScopeBrandImport
                        brands={brands}
                        onSelect={ids => setDraftBrands(current => Array.from(new Set([...current, ...ids])))}
                    />
                </DlpField>
            )}

            <DlpField label={t('dlpConfiguration.scopes.countries')} hint={t('dlpConfiguration.scopes.countriesHint')}>
                <LookupMultiSelect
                    options={countries}
                    value={draft.countries ?? []}
                    onChange={v => patch({ countries: v.length ? v : null })}
                    placeholder={t('dlpConfiguration.scopes.selectCountries')}
                />
            </DlpField>

            {isDirty && <SaveBar onClick={() => setConfirm('save')} />}
            {confirm === 'save' && (
                <ConfirmDialog
                    title={t('dlpConfiguration.scopes.saveTitle')}
                    message={t('dlpConfiguration.scopes.saveMessage', { scope: draft.name })}
                    confirmLabel={t('general.save')}
                    onConfirm={save}
                    onCancel={() => setConfirm(null)}
                />
            )}
            {confirm === 'delete' && (
                <ConfirmDialog
                    title={t('dlpConfiguration.scopes.deleteTitle')}
                    message={t('dlpConfiguration.scopes.deleteMessage', { scope: scope.name })}
                    confirmLabel={t('general.delete')}
                    onConfirm={remove}
                    onCancel={() => setConfirm(null)}
                    danger
                />
            )}
        </div>
    );
}
